import { utils, writeFileXLSX } from "xlsx";
import type { CellObject, WorkBook, WorkSheet } from "xlsx";
import type Commune from "models/Commune";
import type User from "models/User";
import agentService from "services/agentService";
import type { Declaration } from "models/Declaration";
import siegeLesions from "data/siegeLesions";
import type { Securite } from "models/Securite";
import declarationService from "./declarationService";
import type Lesion from "models/Lesion";
import TypeAccident from "constants/TypeAccident";
import { dateUtil } from "@sdeapps/react-core";
import risquesSecurites from "data/risquesSecurites";
import { routesConfig } from "app-config";
import graphService from "./graphService";

async function exportDeclarations(
  declarations: Array<Declaration>,
  communes: Array<Commune>,
  agents: Array<User>,
  filename: string | null = null
): Promise<void> {
  const header = [
    "Id", // A
    "", // B
    "Date accident", // C
    "Année", // D
    "Catégorie", // E
    "Nom agent", // F
    "Fonction", // G
    "Description Circonstances", // H
    "Risques/Conséquences", // I
    "Nature du risque", // J
    "Siège des lésions", // K
    "Nature des lésions", // L
    "", // M
    "Action immédiate et sécurisation", // N
    "Proposition de solution(s)", // O
    "Territoire de l'accident", // P
    "Commune", // Q
    "Rue", // R
    "Type d'ouvrage", // S
    "Service d'affectation", // T
    "", // U
    "Photos", // V
    "", // W
    "", // X
    "", // Y
    "Déclarant", // Z
    "Manager Déclarant", // AA
    "Manager Victime", // AB
    "Confidentielle", // AC
    "Heure de l'accident", // AD
  ];

  const data: Array<Array<string>> = [header];

  await AddDeclarations(data, declarations, agents, communes);

  const workSheet: WorkSheet = utils.aoa_to_sheet(data);

  transformColumnIntoHyperlinks(data, workSheet, "V", 1);

  const workbook: WorkBook = utils.book_new();
  utils.book_append_sheet(workbook, workSheet, "Liste des déclarations");

  writeFileXLSX(workbook, `${filename ?? "declarations"}.xlsx`);
}

function exportRegistreAccidentsBenins(
  declarations: Array<Securite>,
  communes: Array<Commune>,
  agents: Array<User>,
  filename: string | null = null
): void {
  const headers = [
    [
      "N° d'ordre",
      "Date d'inscription dans le registre",
      "Nom, prénom, Matricule interne de la victime",
      "ACCIDENT",
      "",
      "",
      "",
      "",
      "",
      "",
      "Signature du donneur de soins",
      "Signature de la victime",
      "OBSERVATIONS (Date de la DAT éventuellement)",
    ],
    [
      "",
      "",
      "",
      "Date et heure",
      "Lieu",
      "Circonstances détaillées (indiquer, le cas échéant l'appareil, la machine ou le moyen de locomotion utilisé)",
      "Siège des lésions (indiquer s'il y a lieu droite ou gauche)",
      "Nature des lésions",
      "Nom et adresse des témoins ou de la première personne avisée",
      "Nom et adresse des tiers impliqués extérieurs à l'établissement",
      "",
      "",
      "",
    ],
  ];

  const data: Array<Array<string>> = [...headers];

  AddAccidentsBeninsData(data, declarations, agents, communes);

  const workSheet: WorkSheet = utils.aoa_to_sheet(data);
  const workbook: WorkBook = utils.book_new();
  utils.book_append_sheet(workbook, workSheet, "Registre des accidents benins");

  // Fusion des cellules du header
  workSheet["!merges"] = [
    { s: { c: 0, r: 0 }, e: { c: 0, r: 1 } },
    { s: { c: 1, r: 0 }, e: { c: 1, r: 1 } },
    { s: { c: 2, r: 0 }, e: { c: 2, r: 1 } },
    { s: { c: 3, r: 0 }, e: { c: 9, r: 0 } },
    { s: { c: 10, r: 0 }, e: { c: 10, r: 1 } },
    { s: { c: 11, r: 0 }, e: { c: 11, r: 1 } },
    { s: { c: 12, r: 0 }, e: { c: 12, r: 1 } },
  ];

  writeFileXLSX(workbook, `${filename ?? "registre"}.xlsx`);
}

async function AddDeclarations(
  data: Array<Array<string>>,
  declarations: Array<Declaration>,
  agents: Array<User>,
  communes: Array<Commune>
): Promise<void> {
  declarations.sort((a: Declaration, b: Declaration) => a.displayNumber - b.displayNumber);

  for (const dec of declarations) {
    const sec = dec as Securite;
    const victim: User | undefined =
      agentService.getById(agents, dec.victimeId) ??
      declarationService.getSnapshotVictim(dec) ??
      agentService.getById(agents, dec.creatorId);

    const creator = agentService.getById(agents, dec.creatorId) ?? victim;
    const victimManager = await graphService.getManagerByAgent(dec.victimeId);
    const creatorManager = (await graphService.getManagerByAgent(dec.creatorId)) ?? victimManager;

    let risque: string | null = null;
    risquesSecurites.forEach((r) => {
      if (sec.risque === r.label) risque = `${r.index} - ${r.label}`;
    });
    if (dec.id == null) {
      console.warn("dec.id is null in ", dec);
      return;
    }

    data.push([
      dec.displayNumber.toString(), // A
      "", // B
      dateUtil.format(dec.dateEtHeure, "dd/MM/yyyy"), // C
      dateUtil.getYear(dec.dateEtHeure), // D
      declarationService.getTypeDeclarationLabelFromDeclaration(dec), // E
      victim?.displayName ?? "", // F
      victim?.jobTitle ?? "", // G
      `${dec.precisions ?? ""} / ${dec.activiteEnCours ?? ""} / ${dec.commentaire ?? ""}`, // H
      sec?.consequences ?? "", // I
      risque ?? "", // J
      dec?.lesions?.map((lesion) => writeLesion(lesion))?.join(" / ") ?? "", // K
      dec?.lesions?.map((lesion) => lesion.typeLesion)?.join(" / ") ?? "", // L
      "", // M
      sec?.actions ?? "", // N
      sec?.proposition ?? "", // O
      dec.territoire, // P
      communes.find((v: Commune) => v.code === dec.communeInsee)?.nom ?? dec.communeName, // Q
      dec.rue, // R
      dec.ouvrageType === "NON" ? "" : dec.ouvrageType, // S
      victim?.department ?? "", // T
      "", // U
      (dec.photos?.length ?? 0) > 0 ? getFullUrlForDeclaration(dec) : "", // V
      "", // W
      "", // X
      "", // Y
      creator?.displayName ?? "", // Z
      creatorManager?.displayName ?? "", // AA
      victimManager?.displayName ?? "", // AB
      dec.isConfidentiel ? "OUI" : "NON", // AC
      dateUtil.format(dec.dateEtHeure, "HH:mm"), // AD
    ]);
  }
}

function getFullUrlForDeclaration(dec: Declaration): string {
  if (dec.id == null) {
    throw Error("dec.id is null");
  }
  return `${window.location.origin}${
    declarationService.isAccident(dec)
      ? routesConfig.accidentPhotos.getParameterPath(dec.id)
      : routesConfig.securitePhotos.getParameterPath(dec.id)
  }`;
}

function AddAccidentsBeninsData(
  data: Array<Array<string>>,
  accidentsBenins: Array<Securite>,
  agents: Array<User>,
  communes: Array<Commune>
): void {
  accidentsBenins.sort(
    (a: Securite, b: Securite) =>
      (a?.accidentBeninDisplayNumber ?? 0) - (b?.accidentBeninDisplayNumber ?? 0)
  );

  accidentsBenins.forEach((accidentBenin) => {
    if (accidentBenin.accidentBeninSauveteurId == null) {
      console.warn("accidentBenin.accidentBeninSauveteurId is null in ", accidentBenin);
      return;
    }
    if (accidentBenin.accidentBeninDisplayNumber == null) {
      console.warn("accidentBenin.accidentBeninDisplayNumber is null in ", accidentBenin);
      return;
    }

    const victim: User | undefined =
      agentService.getById(agents, accidentBenin.victimeId) ??
      declarationService.getSnapshotVictim(accidentBenin);
    const sauveteur: User | undefined = agentService.getById(
      agents,
      accidentBenin.accidentBeninSauveteurId
    );

    data.push([
      accidentBenin.accidentBeninDisplayNumber.toString(),
      dateUtil.format(accidentBenin.creationDate, "dd/MM/yyyy"),
      `${victim?.displayName ?? ""} ${victim?.employeeId ?? ""}`,
      dateUtil.format(accidentBenin.dateEtHeure, "dd/MM/yyyy HH:mm"),
      `${accidentBenin.rue ?? ""} ${
        communes.find((v: Commune) => v.code === accidentBenin.communeInsee)?.nom ??
        accidentBenin.communeName
      }`,
      `${accidentBenin.precisions ?? ""} / ${accidentBenin.activiteEnCours ?? ""}`,
      accidentBenin?.lesions?.map((lesion) => writeLesion(lesion))?.join(" / ") ?? "",
      accidentBenin?.lesions?.map((lesion) => lesion.typeLesion)?.join(" / ") ?? "",
      accidentBenin?.temoin ?? "",
      accidentBenin?.tiers ?? "",
      getSSTSignature(accidentBenin, sauveteur),
      getVictimSignature(accidentBenin, victim),
      getObservations(accidentBenin),
    ]);
  });
}

function getVictimSignature(accidentBenin: Securite, victim?: User): string {
  if (accidentBenin.accidentBeninConfirmationVictime) {
    return victim?.displayName ?? accidentBenin.victimeId;
  }
  return "";
}

function getSSTSignature(accidentBenin: Securite, sauveteur?: User): string {
  if (accidentBenin.accidentBeninConfirmationSauveteur) {
    return sauveteur?.displayName ?? accidentBenin.accidentBeninSauveteurId ?? "";
  }
  return "";
}

function getObservations(accidentBenin: Securite): string {
  if (
    (accidentBenin.isRequalificationOrigin ?? false) &&
    (accidentBenin.requalification?.type === TypeAccident.TRAJET ||
      accidentBenin.requalification?.type === TypeAccident.TRAVAIL)
  ) {
    return `Date de la DAT : ${dateUtil.format(
      accidentBenin.requalification.creationDate,
      "dd/MM/yyyy HH:mm"
    )}. `;
  }

  return "";
}

function writeLesion(lesion: Lesion): string {
  let s =
    siegeLesions.find((siege) => siege.key === lesion.siegeLesion)?.value ?? lesion.siegeLesion;
  if (lesion.gauche) {
    s += " Gauche";
    if (lesion.droite) {
      s += " et";
    }
  }
  if (lesion.droite) {
    s += " Droite";
  }

  return s;
}

function transformColumnIntoHyperlinks(
  data: Array<Array<string>>,
  workSheet: WorkSheet,
  colName: string,
  headerRowNumber: number = 0
): void {
  data.forEach((_v, i) => {
    const cell: CellObject | undefined = workSheet[`${colName}${i + 1 + headerRowNumber}`];
    if (cell?.v != null && cell.v !== "") {
      workSheet[`${colName}${i + 2}`].l = { Target: cell.v };
    }
  });
}

const excelService = {
  exportDeclarations,
  exportRegistreAccidentsBenins,
};

export default excelService;
